import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertCircle, Banknote, CalendarRange, Inbox } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { fmt, StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { canSee } from "@/lib/sections";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const showEnquiries = canSee(user, "/enquiries");

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const [calMonth, setCalMonth] = useState(new Date().toISOString().slice(0, 7));
  const [calDays, setCalDays] = useState({});
  useEffect(() => {
    api.get("/bookings/calendar", { params: { month: calMonth } }).then((r) => setCalDays(r.data.days)).catch(() => {});
  }, [calMonth]);

  const shiftMonth = (dir) => {
    const [y, m] = calMonth.split("-").map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    setCalMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  if (!data) {
    return <div data-testid="dashboard-loading" className="py-20 text-center text-sm text-muted-foreground">Loading command center…</div>;
  }

  const kpis = [
    ...(showEnquiries ? [{ label: "Pending Enquiries", value: data.pending_enquiries, icon: Inbox, testid: "kpi-pending-enquiries" }] : []),
    { label: "Upcoming Events", value: data.upcoming_count, icon: CalendarRange, testid: "kpi-upcoming-events" },
    { label: "Confirmed Revenue", value: fmt(data.revenue), icon: Banknote, testid: "kpi-revenue", accent: true },
    { label: "Deposits Outstanding", value: fmt(data.deposits_outstanding), icon: AlertCircle, testid: "kpi-deposits", warn: true },
  ];

  const fleetTotal = Math.max(1, (data.fleet.active || 0) + (data.fleet.maintenance || 0) + (data.fleet.deployed || 0));

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Operations Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">Live picture of enquiries, events, revenue and fleet readiness.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k, i) => (
          <motion.div
            key={k.testid}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06, duration: 0.25 }}
            data-testid={k.testid}
            className="border border-border bg-white p-5 transition-transform duration-200 hover:-translate-y-[2px]"
          >
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{k.label}</p>
              <k.icon className={`h-4 w-4 ${k.accent ? "text-brand" : k.warn ? "text-amber-600" : "text-muted-foreground"}`} />
            </div>
            <p className={`mt-3 font-heading text-3xl font-black tracking-tight ${k.accent ? "text-brand" : ""}`}>{k.value}</p>
          </motion.div>
        ))}
      </div>

      <div className="max-w-sm border border-border bg-white p-5" data-testid="calendar-widget">
        {(() => {
          const [y, m] = calMonth.split("-").map(Number);
          const daysInMonth = new Date(y, m, 0).getDate();
          const firstDow = (new Date(y, m - 1, 1).getDay() + 6) % 7;
          const cells = [...Array(firstDow).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
          return (
            <>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">{new Date(y, m - 1).toLocaleString("en-GB", { month: "long", year: "numeric" })}</p>
                <div className="flex gap-1">
                  <button type="button" data-testid="cal-prev-month" onClick={() => shiftMonth(-1)} className="flex h-8 w-8 items-center justify-center border border-border text-xs hover:bg-muted">‹</button>
                  <button type="button" data-testid="cal-next-month" onClick={() => shiftMonth(1)} className="flex h-8 w-8 items-center justify-center border border-border text-xs hover:bg-muted">›</button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-7 gap-1 text-center text-[11px]">
                {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <span key={i} className="font-bold text-muted-foreground">{d}</span>)}
                {cells.map((day, i) => {
                  const key = day ? `${calMonth}-${String(day).padStart(2, "0")}` : "";
                  const count = day ? calDays[key] || 0 : 0;
                  return (
                    <div key={i} data-testid={day ? `cal-day-${key}` : undefined} className="py-1">
                      {day || ""}
                      {count > 0 && <span className="mx-auto mt-0.5 block h-1.5 w-1.5 rounded-full bg-brand" title={`${count} booking${count > 1 ? "s" : ""}`} />}
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="border border-border bg-white lg:col-span-2">
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <h2 className="font-heading text-sm font-bold uppercase tracking-[0.15em]">Upcoming Events</h2>
            <Link to="/bookings" data-testid="view-all-bookings-link" className="px-1 py-2 text-xs font-semibold text-brand hover:text-brand-dark">View all</Link>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Ref</th>
                <th className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Customer</th>
                <th className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Date</th>
                <th className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Venue</th>
                <th className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.upcoming_events.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-muted-foreground">No upcoming events scheduled.</td></tr>
              )}
              {data.upcoming_events.map((b) => (
                <tr key={b.id} className="border-b border-border transition-colors duration-150 last:border-0 hover:bg-slate-50">
                  <td className="px-5 py-3 font-mono text-xs font-semibold">
                    <Link data-testid={`dashboard-booking-${b.booking_ref}`} to={`/bookings/${b.id}`} className="text-brand hover:underline">{b.booking_ref}</Link>
                  </td>
                  <td className="px-5 py-3">{b.customer_name}</td>
                  <td className="px-5 py-3 tabular-nums">{b.event_date}</td>
                  <td className="px-5 py-3 text-muted-foreground">{b.venue_name || "—"}</td>
                  <td className="px-5 py-3"><StatusBadge status={b.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        <div className="space-y-4">
          <div className="border border-border bg-white p-5" data-testid="fleet-status-widget">
            <h2 className="font-heading text-sm font-bold uppercase tracking-[0.15em]">Fleet Readiness</h2>
            <div className="mt-4 space-y-3">
              {["active", "deployed", "maintenance"].map((s) => (
                <div key={s}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="font-semibold uppercase tracking-[0.15em] text-muted-foreground">{s}</span>
                    <span className="font-bold tabular-nums" data-testid={`fleet-count-${s}`}>{data.fleet[s] || 0}</span>
                  </div>
                  <div className="h-1.5 w-full bg-muted">
                    <div
                      className={`h-full ${s === "active" ? "bg-brand" : s === "deployed" ? "bg-blue-600" : "bg-amber-500"}`}
                      style={{ width: `${((data.fleet[s] || 0) / fleetTotal) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {showEnquiries && (
          <div className="border border-border bg-white" data-testid="recent-enquiries-widget">
            <div className="border-b border-border px-5 py-3">
              <h2 className="font-heading text-sm font-bold uppercase tracking-[0.15em]">Latest Enquiries</h2>
            </div>
            <div className="divide-y divide-border">
              {data.recent_enquiries.map((e) => (
                <Link key={e.id} to="/enquiries" className="flex items-center justify-between px-5 py-3 transition-colors duration-150 hover:bg-slate-50" data-testid={`recent-enquiry-${e.id}`}>
                  <div>
                    <p className="text-sm font-medium">{e.contact_name}</p>
                    <p className="text-xs text-muted-foreground tabular-nums">{e.event_date} · {e.guest_count} guests</p>
                  </div>
                  <StatusBadge status={e.status} />
                </Link>
              ))}
              {data.recent_enquiries.length === 0 && <p className="px-5 py-6 text-sm text-muted-foreground">No enquiries yet.</p>}
            </div>
          </div>
          )}

          <div className="border border-border bg-white" data-testid="tasks-due-widget">
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <h2 className="font-heading text-sm font-bold uppercase tracking-[0.15em]">Tasks Due</h2>
              <Link to="/tasks" data-testid="view-all-tasks-link" className="px-1 py-2 text-xs font-semibold text-brand hover:text-brand-dark">View all</Link>
            </div>
            <div className="divide-y divide-border">
              {(data.tasks_due || []).map((t) => (
                <Link key={t.id} to="/tasks" className="flex items-center justify-between px-5 py-3 transition-colors duration-150 hover:bg-slate-50" data-testid={`dashboard-task-${t.id}`}>
                  <p className="text-sm font-medium">{t.title}</p>
                  <span className="text-xs text-muted-foreground tabular-nums">{t.due_date}</span>
                </Link>
              ))}
              {(data.tasks_due || []).length === 0 && <p className="px-5 py-6 text-sm text-muted-foreground">Nothing due. Nice.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
